import { Link } from "react-router-dom";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { calculateMeetupPricing, formatMoney } from "../lib/meetupPricing";
import { Compass, MapPin, MessageCircleMore, ShieldCheck, X } from "lucide-react";

export default function MeetupPinSheet({
  pin,
  businessView = false,
  onClose,
  onOpenChat,
}) {
  if (!pin) return null;

  const pricing = calculateMeetupPricing({
    basePrice: pin.base_price_usd || 75,
    guests: 2,
    country: pin.country || "barbados",
  });

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[5000] md:absolute md:inset-auto md:bottom-4 md:left-4 md:right-4 md:max-w-sm animate-in slide-in-from-bottom duration-200"
      role="dialog"
      aria-label={`Details for ${pin.name}`}
    >
      <div className="rounded-t-[1.5rem] md:rounded-2xl border border-cyan-100 bg-slate-950/95 text-white shadow-2xl backdrop-blur-md p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-cyan-300 font-semibold flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {pin.parish ? `${pin.parish} · ` : ""}
              {pin.island}
            </p>
            <h4 className="text-lg font-semibold leading-tight mt-1">{pin.name}</h4>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 hover:bg-white/10"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {pin.description && (
          <p className="text-sm text-slate-300 mb-3 line-clamp-3">{pin.description}</p>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          <Badge className="bg-white/10 text-white border-0 capitalize">{pin.kind || "spot"}</Badge>
          {pin.is_featured && (
            <Badge className="bg-emerald-500/20 text-emerald-200 border-0 inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" /> Verified
            </Badge>
          )}
          {pin.tier && (
            <Badge className="bg-cyan-500/20 text-cyan-100 border-0">{pin.tier}</Badge>
          )}
        </div>

        {!businessView ? (
          <div className="space-y-1.5 text-xs mb-4">
            <div className="flex justify-between">
              <span className="text-slate-400">Total price</span>
              <span className="font-semibold">{formatMoney(pricing.totalPrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">Includes VAT/GCT</span>
              <span>{formatMoney(pricing.vatCollected)}</span>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5 text-xs mb-4">
            <div className="flex justify-between">
              <span className="text-slate-400">Your payout</span>
              <span className="font-semibold">{formatMoney(pricing.businessPayout)}</span>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {onOpenChat && (
            <Button
              type="button"
              className="flex-1 rounded-full min-w-[120px]"
              onClick={() => onOpenChat(pin)}
            >
              <MessageCircleMore className="w-4 h-4 mr-2" />
              Map Friend
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            className="rounded-full border-white/20 text-white hover:bg-white/10"
            asChild
          >
            <Link to={`/discover?country=${pin.country || "barbados"}&pin=${encodeURIComponent(pin.pin_id || "")}`}>
              <Compass className="w-4 h-4 mr-2 inline" />
              Explore
            </Link>
          </Button>
          {pin.google_maps_url && (
            <Button
              type="button"
              variant="outline"
              className="rounded-full border-white/20 text-white hover:bg-white/10"
              asChild
            >
              <a href={pin.google_maps_url} target="_blank" rel="noreferrer">
                Directions
              </a>
            </Button>
          )}
        </div>
        <p className="text-[10px] text-slate-500 mt-3 text-center">
          {pin.lat?.toFixed(4)}, {pin.lng?.toFixed(4)} · verified coordinates
        </p>
      </div>
    </div>
  );
}
